import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import styles from "./LetsGoLanding.module.css";

const LogoIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={styles.logoIcon}>
    <path d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
    <path d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
  </svg>
);

const navigationLinks = [
  { id: "features", label: "Features" },
  { id: "how-it-works", label: "How It Works" },
  { id: "faq", label: "FAQ" },
];

const features = [
  {
    icon: "🗺️",
    title: "Discover Nearby Events",
    description: "See what's happening around you right now — concerts, markets, pop-ups, and more. Sorted by distance.",
  },
  {
    icon: "📸",
    title: "Rich Event Details",
    description: "Full photo galleries, interactive maps, organizer info, and attendee counts — everything you need to decide in seconds.",
  },
  {
    icon: "🔍",
    title: "Smart Search & Filter",
    description: "Search by city, neighborhood, or event name. Find exactly what you're looking for without scrolling forever.",
  },
  {
    icon: "📅",
    title: "Add to Calendar",
    description: "One tap to save an event to your iPhone calendar. Never double-book or forget a show again.",
  },
  {
    icon: "❤️",
    title: "Save Favorites",
    description: "Bookmark events you're interested in and come back to them later — even when you're offline.",
  },
  {
    icon: "📍",
    title: "Interactive Maps",
    description: "See exactly where an event is happening. Tap to open in Apple Maps and get turn-by-turn directions.",
  },
];

const steps = [
  {
    number: "01",
    title: "Open the app",
    description: "Sign up in seconds. Let's Go finds events near you automatically based on your location.",
  },
  {
    number: "02",
    title: "Browse your city",
    description: "Scroll featured events, search by name or neighborhood, or explore the map to find something new.",
  },
  {
    number: "03",
    title: "Go — and never miss out",
    description: "Tap into full event details, save it to your calendar, and get directions. Just show up.",
  },
];

const faqs = [
  {
    question: "What kinds of events are on Let's Go?",
    answer: "Music and live shows, food markets, pop-ups, art events, fitness classes, community gatherings — anything happening in your city. We're focused on discovery, not just ticketing.",
  },
  {
    question: "Is it free?",
    answer: "Let's Go will launch with a free tier covering everything you need to discover and attend events. A premium tier is planned with features like personalized recommendations and event alerts.",
  },
  {
    question: "How does it find events near me?",
    answer: "Let's Go uses your location to surface events nearby. You can also search by city or neighborhood manually if you're planning ahead for a trip.",
  },
  {
    question: "When does it launch?",
    answer: "We're finishing the iOS app now. Join the waitlist and you'll be first to know the moment it drops on the App Store.",
  },
];

export const LetsGoLanding: React.FC = () => {
  const [isScrolled, setIsScrolled] = React.useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [openFaq, setOpenFaq] = React.useState<number | null>(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth" });
    setIsMobileMenuOpen(false);
  };

  return (
    <div className={styles.page}>
      <a href="#main-content" className={styles.skipLink}>Skip to main content</a>

      <nav className={`${styles.nav} ${isScrolled ? styles.navScrolled : ""}`}>
        <div className={styles.navContainer}>
          <Link to="/lets-go" className={styles.logo}>
            <LogoIcon />
            <span>Let's Go</span>
          </Link>

          <ul className={styles.navLinks}>
            {navigationLinks.map((link) => (
              <li key={link.id}>
                <button onClick={() => scrollToSection(link.id)} className={styles.navLink}>
                  {link.label}
                </button>
              </li>
            ))}
          </ul>

          <Link to="/lets-go/waitlist" className={styles.navCta}>
            Join Waitlist
          </Link>

          <button
            className={styles.mobileMenuButton}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
          >
            <span className={`${styles.hamburger} ${isMobileMenuOpen ? styles.hamburgerOpen : ""}`} />
          </button>
        </div>

        <div className={`${styles.mobileMenu} ${isMobileMenuOpen ? styles.mobileMenuOpen : ""}`}>
          <ul className={styles.mobileNavLinks}>
            {navigationLinks.map((link) => (
              <li key={link.id}>
                <button onClick={() => scrollToSection(link.id)} className={styles.mobileNavLink}>
                  {link.label}
                </button>
              </li>
            ))}
            <li>
              <Link to="/lets-go/waitlist" className={styles.mobileCta} onClick={() => setIsMobileMenuOpen(false)}>
                Join the Waitlist
              </Link>
            </li>
          </ul>
        </div>
      </nav>

      <main id="main-content" role="main" tabIndex={-1}>
        {/* Hero */}
        <section className={styles.hero}>
          <div className={styles.heroGlow} />
          <div className={styles.heroContainer}>
            <div className={styles.badge}>Coming Soon to iOS</div>
            <h1 className={styles.heroHeadline}>
              Your city is buzzing.<br />
              <span className={styles.heroAccent}>Don't miss a thing.</span>
            </h1>
            <p className={styles.heroSubheadline}>
              Let's Go is the event discovery app for iPhone — concerts, markets, pop-ups, and local events near you, with full details, maps, and one-tap calendar saving.
            </p>
            <div className={styles.heroCtas}>
              <Link to="/lets-go/waitlist" className={styles.primaryCta}>
                Join the Waitlist
              </Link>
              <button onClick={() => scrollToSection("how-it-works")} className={styles.secondaryCta}>
                See how it works
              </button>
            </div>
            <p className={styles.heroNote}>Free to join. One email when we launch. No spam.</p>
          </div>
        </section>

        {/* Features */}
        <section id="features" className={styles.features}>
          <div className={styles.sectionContainer}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Everything you need to get out there</h2>
              <p className={styles.sectionSubtitle}>
                Discover, save, and attend local events — without the friction.
              </p>
            </div>
            <div className={styles.featuresGrid}>
              {features.map((f) => (
                <div key={f.title} className={styles.featureCard}>
                  <div className={styles.featureEmoji}>{f.icon}</div>
                  <h3 className={styles.featureTitle}>{f.title}</h3>
                  <p className={styles.featureDescription}>{f.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section id="how-it-works" className={styles.howItWorks}>
          <div className={styles.sectionContainer}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Up and running in seconds</h2>
              <p className={styles.sectionSubtitle}>No setup. No friction. Three steps to finding your next event.</p>
            </div>
            <div className={styles.stepsGrid}>
              {steps.map((step) => (
                <div key={step.number} className={styles.stepCard}>
                  <div className={styles.stepNumber}>{step.number}</div>
                  <h3 className={styles.stepTitle}>{step.title}</h3>
                  <p className={styles.stepDescription}>{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className={styles.faq}>
          <div className={styles.sectionContainer}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Common questions</h2>
            </div>
            <div className={styles.faqList}>
              {faqs.map((item, i) => (
                <div key={i} className={`${styles.faqItem} ${openFaq === i ? styles.faqItemOpen : ""}`}>
                  <button
                    className={styles.faqQuestion}
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    aria-expanded={openFaq === i}
                  >
                    <span>{item.question}</span>
                    <span className={styles.faqChevron}>{openFaq === i ? "−" : "+"}</span>
                  </button>
                  {openFaq === i && (
                    <p className={styles.faqAnswer}>{item.answer}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className={styles.cta}>
          <div className={styles.ctaContainer}>
            <h2 className={styles.ctaTitle}>Ready to explore your city?</h2>
            <p className={styles.ctaSubtitle}>
              Join the waitlist and be first in line when Let's Go launches on iOS.
            </p>
            <Link to="/lets-go/waitlist" className={styles.primaryCta}>
              Join the Waitlist
            </Link>
          </div>
        </section>
      </main>

      <footer className={styles.footer}>
        <div className={styles.footerContainer}>
          <div className={styles.footerBrand}>
            <Link to="/lets-go" className={styles.footerLogo}>
              <LogoIcon />
              <span>Let's Go</span>
            </Link>
            <p className={styles.footerTagline}>Discover what's happening near you.</p>
          </div>
          <div className={styles.footerLinks}>
            <div className={styles.footerColumn}>
              <h3>Product</h3>
              <ul>
                <li><button onClick={() => scrollToSection("features")}>Features</button></li>
                <li><button onClick={() => scrollToSection("how-it-works")}>How It Works</button></li>
                <li><button onClick={() => scrollToSection("faq")}>FAQ</button></li>
              </ul>
            </div>
            <div className={styles.footerColumn}>
              <h3>Company</h3>
              <ul>
                <li><Link to="/">Dixon App Development</Link></li>
                <li><a href="mailto:dixonappdevelopment@gmail.com">Contact</a></li>
              </ul>
            </div>
          </div>
        </div>
        <div className={styles.footerBottom}>
          <p>&copy; {new Date().getFullYear()} Dixon App Development. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};
